/**
 * Function Search Component - Advanced search with filters and sorting
 */

import React, { useState, useEffect, useMemo } from 'react';
import { MagnifyingGlassIcon, FunnelIcon, XMarkIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { SearchParams, SearchFilters, FunctionCategory } from '../../services/marketplace';

interface FunctionSearchProps {
  categories: FunctionCategory[];
  onSearch: (params: SearchParams) => void;
  resultCount?: number;
  initialParams?: SearchParams;
}

export const FunctionSearch: React.FC<FunctionSearchProps> = ({
  categories,
  onSearch,
  resultCount,
  initialParams = {}
}) => {
  const [query, setQuery] = useState(initialParams.query || '');
  const [filters, setFilters] = useState<SearchFilters>(initialParams.filters || {});
  const [sortBy, setSortBy] = useState(initialParams.sortBy || 'relevance');
  const [showFilters, setShowFilters] = useState(false);

  // Popular tags for quick selection
  const popularTags = useMemo(() => [
    'AI/ML', 'Data Processing', 'Web API', 'Image Processing', 'Text Analysis',
    'Database', 'Authentication', 'File Processing', 'Email', 'Notification'
  ], []);

  const sortOptions = [
    { value: 'relevance', label: 'Relevance' },
    { value: 'popularity', label: 'Most Popular' },
    { value: 'rating', label: 'Highest Rated' },
    { value: 'newest', label: 'Newest' },
    { value: 'updated', label: 'Recently Updated' }
  ];

  const pricingOptions = [
    { value: 'free', label: 'Free' },
    { value: 'paid', label: 'Paid' },
    { value: 'subscription', label: 'Subscription' },
    { value: 'usage', label: 'Usage-based' }
  ];

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      const params: SearchParams = {
        query: query.trim() || undefined,
        filters: Object.keys(filters).length > 0 ? filters : undefined,
        sortBy,
        page: 1,
      };

      onSearch(params);
    }, 300);

    return () => clearTimeout(timer);
  }, [query, filters, sortBy, onSearch]);

  const updateFilter = (key: keyof SearchFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const toggleTag = (tag: string) => {
    setFilters(prev => {
      const currentTags = prev.tags || [];
      const newTags = currentTags.includes(tag)
        ? currentTags.filter(t => t !== tag)
        : [...currentTags, tag];

      return {
        ...prev,
        tags: newTags.length > 0 ? newTags : undefined
      };
    });
  };

  const clearFilters = () => {
    setFilters({});
    setQuery('');
    setSortBy('relevance');
  };

  const activeFilterCount = Object.values(filters).filter(value =>
    value !== undefined && value !== '' &&
    !(Array.isArray(value) && value.length === 0)
  ).length;

  return (
    <div className="space-y-4">
      {/* Main Search Bar */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search functions..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="flex gap-2">
          {/* Sort Dropdown */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as any)}
            className="px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {sortOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`relative px-4 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors ${
              showFilters ? 'bg-blue-50 border-blue-300' : ''
            }`}
          >
            <div className="flex items-center gap-2">
              <FunnelIcon className="w-5 h-5" />
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </div>
          </button>
        </div>
      </div>

      {/* Results Count */}
      {resultCount !== undefined && (
        <div className="flex items-center justify-between text-sm text-gray-600">
          <span>{resultCount.toLocaleString()} functions found</span>
          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              className="text-blue-600 hover:text-blue-700 flex items-center gap-1"
            >
              <XMarkIcon className="w-4 h-4" />
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Advanced Filters Panel */}
      {showFilters && (
        <div className="bg-gray-50 rounded-xl p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">Advanced Filters</h3>
            <button
              onClick={() => setShowFilters(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={filters.category || ''}
                onChange={(e) => updateFilter('category', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name} ({category.count})
                  </option>
                ))}
              </select>
            </div>

            {/* Pricing Model Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pricing
              </label>
              <select
                value={filters.pricing || ''}
                onChange={(e) => updateFilter('pricing', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Any Pricing</option>
                {pricingOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Rating Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Rating
              </label>
              <select
                value={filters.minRating || ''}
                onChange={(e) => updateFilter('minRating', e.target.value ? Number(e.target.value) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Any Rating</option>
                <option value="4">4+ Stars</option>
                <option value="3">3+ Stars</option>
                <option value="2">2+ Stars</option>
                <option value="1">1+ Stars</option>
              </select>
            </div>
          </div>

          {/* Featured Toggle */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.featured || false}
                onChange={(e) => updateFilter('featured', e.target.checked || undefined)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Featured functions only
              </span>
            </label>
          </div>

          {/* Popular Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Popular Tags
            </label>
            <div className="flex flex-wrap gap-2">
              {popularTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => toggleTag(tag)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    filters.tags?.includes(tag)
                      ? 'bg-blue-100 text-blue-800 border border-blue-300'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Selected Tags Display */}
          {filters.tags && filters.tags.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selected Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {filters.tags.map(tag => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                  >
                    {tag}
                    <button
                      onClick={() => toggleTag(tag)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      <XMarkIcon className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Tags (visible when not showing filters) */}
      {!showFilters && (
        <div className="flex flex-wrap gap-2">
          {popularTags.slice(0, 6).map(tag => (
            <button
              key={tag}
              onClick={() => toggleTag(tag)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                filters.tags?.includes(tag)
                  ? 'bg-blue-100 text-blue-800 border border-blue-300'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tag}
            </button>
          ))}

          {popularTags.length > 6 && (
            <button
              onClick={() => setShowFilters(true)}
              className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 flex items-center gap-1"
            >
              <AdjustmentsHorizontalIcon className="w-4 h-4" />
              More filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FunctionSearch;
